import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Image as ImageIcon, Video, Sparkles, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || (
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    ? 'http://localhost:8000'
    : 'https://crapule-bxl-production.up.railway.app'
);
const API = `${BACKEND_URL}/api`;

const Media = ({
  featuredMedia,
  lastPastEvent,
  lastPastMedia,
  nextEvent,
  nextEventMedia,
  siteMediaEnabled = true,
  loading
}) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxItems, setLightboxItems] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const openLightbox = useCallback((items, index = 0) => {
    setLightboxItems(items);
    setLightboxIndex(index);
    setLightboxOpen(true);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (!lightboxOpen) return;
      if (e.key === 'Escape') setLightboxOpen(false);
      if (e.key === 'ArrowRight') setLightboxIndex(i => (i + 1) % lightboxItems.length);
      if (e.key === 'ArrowLeft') setLightboxIndex(i => (i - 1 + lightboxItems.length) % lightboxItems.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxOpen, lightboxItems.length]);

  if (!siteMediaEnabled) {
    return null;
  }

  if (loading) {
    return (
      <section id="media" className="px-6 py-24 bg-black md:py-32">
        <div className="mx-auto max-w-5xl text-center">
          <p className="text-gray-500">Chargement des médias...</p>
        </div>
      </section>
    );
  }

  const srcFor = (u) => (u && u.startsWith('http') ? u : `${BACKEND_URL}${u || ''}`);

  const toInstagramEmbed = (url) => {
    if (!url) return null;
    try {
      const u = new URL(url);
      if (u.hostname.includes('instagram.com')) {
        const parts = u.pathname.split('/').filter(Boolean);
        if (parts.length >= 2) {
          const kind = parts[0]; // p, reel, tv
          const code = parts[1];
          return `https://www.instagram.com/${kind}/${code}/embed`;
        }
      }
    } catch {}
    return null;
  };

  const gridItem = (item, index, items) => (
    <button
      key={item.id || index}
      onClick={() => openLightbox(items, index)}
      className="overflow-hidden relative bg-gray-900 rounded-lg group"
    >
      <div className="w-full aspect-square md:aspect-video">
        {item.type === 'image' ? (
          <img
            src={srcFor(item.url)}
            alt={item.caption || 'Image'}
            loading="lazy"
            decoding="async"
            className="object-cover w-full h-full transition-transform group-hover:scale-105"
          />
        ) : item.type === 'video' ? (
          <video
            src={srcFor(item.url)}
            className="object-cover w-full h-full"
            preload="none"
            playsInline
            muted
          />
        ) : item.type === 'instagram' ? (
          <iframe
            title={item.caption || `Instagram media ${index + 1}`}
            src={toInstagramEmbed(item.url) || item.url}
            className="w-full h-full"
            loading="lazy"
            allowTransparency="true"
            frameBorder="0"
            scrolling="no"
          />
        ) : null}
      </div>
      <div className="absolute inset-0 bg-gradient-to-t to-transparent opacity-0 transition-opacity group-hover:opacity-100 from-black/60"></div>
      <div className="flex absolute right-0 bottom-0 left-0 justify-between items-center p-3">
        <div className="inline-flex gap-2 items-center px-2 py-1 text-xs text-white rounded bg-black/40">
          {item.type === 'image' ? <ImageIcon size={12} /> : <Video size={12} />}
          <span>{item.type}</span>
        </div>
        {item.caption && (
          <span className="text-xs text-gray-200 truncate max-w-[60%]">{item.caption}</span>
        )}
      </div>
    </button>
  );

  const renderSection = (title, items) => {
    const visibleItems = (items || []).filter(i => i.visible !== false);
    if (visibleItems.length === 0) return null;
    return (
      <div className="mb-10">
        <div className="flex gap-2 items-center mb-4">
          <span className="w-2 h-2 bg-white rounded-full"></span>
          <h3 className="text-sm uppercase tracking-[0.3em] text-gray-400">{title}</h3>
        </div>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
          {visibleItems.map((item, idx, arr) => gridItem(item, idx, arr))}
        </div>
      </div>
    );
  };

  return (
    <section id="media" className="relative px-6 py-24 bg-black bg-gradient-to-b to-gray-950 from-gray-950 md:py-32">
      <div className="relative z-10 mx-auto max-w-5xl">
        <div className="mb-12 text-center">
          <h2 className="mb-2 text-4xl font-black tracking-tight md:text-6xl">LA GALERIE</h2>
          <p className="text-lg text-gray-400">Photos & vidéos des éditions</p>
        </div>

        <div className="mb-12">
          <div className="flex gap-2 items-center mb-4">
            <Sparkles size={16} className="text-white" />
            <h3 className="text-sm uppercase tracking-[0.3em] text-white font-semibold">À la une</h3>
          </div>
          {(!featuredMedia || featuredMedia.length === 0) ? (
            <div className="p-8 text-center text-gray-500 rounded-lg bg-gray-900/40">Aucun média récent</div>
          ) : (
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
              {featuredMedia.slice(0, 6).map((item, idx, arr) => gridItem(item, idx, arr))}
            </div>
          )}
        </div>

        {lastPastEvent && renderSection(`Vol.${lastPastEvent.volume} — ${lastPastEvent.theme}`, lastPastMedia || [])}
        {nextEvent && renderSection(`Vol.${nextEvent.volume} — ${nextEvent.theme || 'Prochainement'}`, nextEventMedia || [])}
      </div>

      {lightboxOpen && lightboxItems.length > 0 && (
        <div className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-sm flex items-center justify-center">
          <div className="px-6 w-full max-w-4xl">
            <div className="overflow-hidden relative bg-black rounded-lg">
              <button
                className="flex absolute top-2 right-2 justify-center items-center w-9 h-9 text-white rounded-full bg-white/10 hover:bg-white/20"
                onClick={() => setLightboxOpen(false)}
                aria-label="Fermer"
              >
                <X size={16} />
              </button>
              <div className="w-full aspect-video">
                {lightboxItems[lightboxIndex].type === 'image' ? (
                  <img
                    src={srcFor(lightboxItems[lightboxIndex].url)}
                    alt={lightboxItems[lightboxIndex].caption || 'Image'}
                    decoding="async"
                    className="object-contain w-full h-full"
                  />
                ) : lightboxItems[lightboxIndex].type === 'video' ? (
                  <video
                    src={srcFor(lightboxItems[lightboxIndex].url)}
                    className="w-full h-full"
                    playsInline
                    controls
                  />
                ) : (
                  <iframe
                    title={lightboxItems[lightboxIndex].caption || 'Instagram media'}
                    src={toInstagramEmbed(lightboxItems[lightboxIndex].url) || lightboxItems[lightboxIndex].url}
                    className="w-full h-full"
                    loading="lazy"
                    allowTransparency="true"
                    frameBorder="0"
                    scrolling="no"
                  />
                )}
              </div>
              <div className="flex justify-between items-center p-4">
                <p className="text-sm text-gray-300">{lightboxItems[lightboxIndex].caption || ''}</p>
                <div className="flex gap-2 items-center">
                  <button
                    className="px-3 py-1 text-white rounded bg-white/10 hover:bg-white/20"
                    onClick={() => setLightboxIndex(i => (i - 1 + lightboxItems.length) % lightboxItems.length)}
                    aria-label="Précédent"
                  >
                    ←
                  </button>
                  <button
                    className="px-3 py-1 text-white rounded bg-white/10 hover:bg-white/20"
                    onClick={() => setLightboxIndex(i => (i + 1) % lightboxItems.length)}
                    aria-label="Suivant"
                  >
                    →
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Media;
